'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { CarouselTrack } from '@/src/components/features/property/ui/Carousel/CarouselTrack';
import { CarouselItem } from '@/src/components/features/property/ui/Carousel/CarouselItem';
import { CarouselIndicators } from '@/src/components/features/property/ui/Carousel/CarouselIndicators';
import { useCarouselNavigation } from '@/src/components/features/property/ui/Carousel/hooks/useCarouselNavigation';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { LucideIcon } from '@/src/components/LucideIcon';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { FloorPlanCard } from './FloorPlanCard';
import type { Floorplan, Unit } from './UnitTable';
import * as styles from './unitsModule.styles';

type DesktopFloorPlanCarouselProps = {
  floorplans: Array<Floorplan>;
  listing: PropertyDetailListing;
  onUnitSelect: (unit: Unit, floorplan: Floorplan) => void;
  onSeeAll: (floorplan: Floorplan) => void;
};

export const DesktopFloorPlanCarousel = ({
  floorplans,
  listing,
  onUnitSelect,
  onSeeAll,
}: DesktopFloorPlanCarouselProps) => {
  const t = useTranslations('hdp');
  const {
    trackRef,
    canScrollLeft,
    canScrollRight,
    currentPage,
    totalPages,
    handlePrevious,
    handleNext,
    scrollToPage,
    handleScroll,
  } = useCarouselNavigation({ gap: 'sm', step: 1 });

  const showControls = totalPages > 1;

  return (
    <div>
      <div className={styles.desktopCarouselRow}>
        <div className={styles.desktopArrowColumn}>
          {showControls && (
            <button
              type="button"
              className={styles.desktopControlButton}
              onClick={handlePrevious}
              disabled={!canScrollLeft}
              aria-label={t('previousFloorPlans')}
            >
              <LucideIcon icon={ChevronLeft} size="sm" />
            </button>
          )}
        </div>

        <div className={styles.desktopCardsArea}>
          <CarouselTrack ref={trackRef} gap="sm" snapAlign="start" onScroll={handleScroll} aria-label={t('floorPlans')}>
            {floorplans.map((fp) => (
              <CarouselItem key={fp.modelId} className={styles.desktopCarouselItem}>
                <FloorPlanCard
                  floorplan={fp}
                  listing={listing}
                  onUnitSelect={onUnitSelect}
                  onSeeAll={onSeeAll}
                  compact
                />
              </CarouselItem>
            ))}
          </CarouselTrack>
        </div>

        <div className={styles.desktopArrowColumn}>
          {showControls && (
            <button
              type="button"
              className={styles.desktopControlButton}
              onClick={handleNext}
              disabled={!canScrollRight}
              aria-label={t('nextFloorPlans')}
            >
              <LucideIcon icon={ChevronRight} size="sm" />
            </button>
          )}
        </div>
      </div>

      {showControls && (
        <CarouselIndicators currentPage={currentPage} totalPages={totalPages} onPageSelect={scrollToPage} />
      )}
    </div>
  );
};
